import { FC, ReactElement, useCallback, useEffect, useMemo } from "react";
import { observer } from "mobx-react";
import { TFunction } from "@/i18n/tt";
import {
  AgendaItemType,
  mapToAgendaItem,
  meetingUrl,
  NoticeActiveView,
  NoticeForShareholderInitializerController,
  toNoticeActiveView,
} from "./generalMeetingDomain";
import { LoggedOnUserInfoDTO } from "@/types/UserInfo";
import { isOk } from "@/common/common";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import { Alert } from "@/components-v2/Alert";
import { NoticeForShareholderView } from "./generalMeetingComponents";
import { noticeForShareholderUrl } from "@/utils/navigation";
import {
  onMessage,
  subscribeToGeneralMeetingParticipantMessages,
} from "./participantMessageHandling";
import {
  useMessageStore,
  useRootStore,
  useTranslation,
  useUserStore,
} from "@/stores/RootStoreContext";
import { MeetingLogin } from "@/meetings/meetingsComponents";
import LoginPage from "@/Login/LoginPage";

const NoticeForShareholderPage: FC<{
  noticeToGeneralMeetingId: string;
  tab?: string;
  matter?: string;
}> = observer(({ noticeToGeneralMeetingId, tab, matter }) => {
  const { t } = useTranslation();
  const history = useRootStore().history;
  const { http, dispatch } = useRootStore();
  const messageStore = useMessageStore();
  const userStore = useUserStore();
  const ctrl = useMemo(
    () =>
      new NoticeForShareholderInitializerController(
        http,
        messageStore,
        dispatch,
        (user: LoggedOnUserInfoDTO) => userStore.setUserInfo(user),
      ),
    [dispatch, http, messageStore, userStore],
  );
  const agendaItem = isOk(ctrl.result)
    ? mapToAgendaItem(ctrl.result.data.response, matter)
    : undefined;

  useEffect(() => {
    ctrl.fetchNoticeForShareholder(noticeToGeneralMeetingId);
  }, [ctrl, noticeToGeneralMeetingId]);

  useEffect(() => {
    if (isOk(ctrl.result) && ctrl.result.data.response.supersededBy) {
      history.replace(noticeForShareholderUrl(ctrl.result.data.response.supersededBy));
    }
  }, [ctrl.result, history]);

  const openAgendaItem = useCallback(
    (item?: AgendaItemType) => {
      if (isOk(ctrl.result)) {
        history.push(meetingUrl(ctrl.result.data.response, NoticeActiveView.meeting, { item }));
      }
    },
    [ctrl.result, history],
  );

  useEffect(() => {
    if (isOk(ctrl.result) && ctrl.result.data.isLiveGeneralMeeting) {
      const response = ctrl.result.data.response;
      const updateSelectedAttendances = ctrl.result.data.updateSelectedAttendances;
      const handleMessage = onMessage({
        response: response,
        messageStore,
        openAgendaItem,
        dispatch: dispatch,
        onRequestPageReload: ctrl.result.data.setRequestPageReload,
        updateSelectedAttendances,
      });
      return subscribeToGeneralMeetingParticipantMessages({
        credential: { "notice-id": response.noticeForShareholderId },
        noticeForShareholderIds: [
          ctrl.result.data.response.noticeForShareholderId,
          ...ctrl.result.data.response.receivedPowerOfAttorneys.map(
            (poa) => poa.noticeForShareholderId,
          ),
        ],
        projectId: ctrl.result.data.response.projectId,
        dispatch: dispatch,
        onMessage: handleMessage,
      });
    }
  }, [ctrl.result, messageStore, t, openAgendaItem, dispatch]);

  return renderNoticePage(
    t,
    ctrl,
    noticeToGeneralMeetingId,
    openAgendaItem,
    toNoticeActiveView(tab),
    agendaItem,
  );
});

function renderNoticePage(
  t: TFunction,
  ctrl: NoticeForShareholderInitializerController,
  noticeToGeneralMeetingId: string,
  openAgendaItem: (agendaItem?: AgendaItemType) => void,
  activeView?: NoticeActiveView,
  agendaItem?: AgendaItemType,
): ReactElement {
  const result = ctrl.result;
  switch (result.state) {
    case "pending":
      return <LoadingIndicator fullscreen />;
    case "failure":
      if (result.error.authStatus) {
        if (result.error.authStatus === "LOGIN_NEEDED") {
          return <LoginPage />;
        } else {
          return (
            <MeetingLogin
              authStatus={result.error.authStatus}
              companyName={result.error.companyName}
              companyLogo={result.error.companyLogo}
              meetingPassword={ctrl.meetingPassword}
              onSubmit={() => {
                ctrl.fetchNoticeForShareholder(noticeToGeneralMeetingId);
              }}
            />
          );
        }
      }
      return <Alert type={"error"} message={t({ key: "noticeForShareholder.loadNoticeError" })} />;

    case "cancelled":
      return <div />;
    case "ok":
      return (
        <NoticeForShareholderView
          controller={result.data}
          activeView={activeView}
          agendaItem={agendaItem}
          openAgendaItem={openAgendaItem}
        />
      );
  }
}

export default NoticeForShareholderPage;
